import React, { useEffect, useState } from "react";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import Logger from "../../../services/Logger";
import { Box, Chip, Grid, IconButton, Pagination, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { trackEvent } from "../../../services/GoogleAnalytics";
import UserService from "../../../services/UserService";
import SubscriptionDialog from "../../subscription/SubscriptionDialog";
import CrossService from "../crossword/CrossService";
import GridService from "../grid/GridService";
import CompletePanel from "./CompletePanel";
import InProgressPanel from "./InProgressPanel";
import { sortPuzzlesFreeFirst } from "../common/utils/PuzzleCardHelpers";
import { getContentLanguage } from "../../../services/LangPrefs";
import { usePagesTranslation } from "../../locales";
import { NOTICE, TEXT } from "../../../dark-theme/palette";
import HomeService from "../../../services/HomeService";
import SlidingPlayService from "../sliding/SlidingPlayService";
import { slidingPlayRoToCardPuzzle } from "../../browse-sliding/slidingBrowseUtils";
import JigsawPlayService from "../jigsaw/JigsawPlayService";
import { jigsawPlayRoToCardPuzzle, getJigsawPlayBoardPath } from "../jigsaw/jigsawBrowseUtils";
import WordWheelService, { wordWheelPlayRoToCard } from "../wordwheel/WordWheelService";

/** Top-level library tabs: In Progress first, then Completed. */
const TAB_IN_PROGRESS = 0;
const TAB_COMPLETED = 1;

const libraryFilterChipSelectedSx = {
    backgroundColor: "rgba(56, 132, 255, 0.22)",
    color: TEXT.primary,
    borderColor: NOTICE.info.border,
    "&:hover": { backgroundColor: "rgba(56, 132, 255, 0.36)" },
};

const libraryPaginationSx = {
    "& .MuiPaginationItem-root.Mui-selected": {
        backgroundColor: "rgba(56, 132, 255, 0.34)",
        color: TEXT.primary,
        "&:hover": { backgroundColor: "rgba(56, 132, 255, 0.48)" },
    },
};

const LibraryMain = () => {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(TAB_IN_PROGRESS);
    // Sub-tab: 0=Jigsaw, 1=Sliding, 2=Crossword, 3=Hidden Message, 4=Word Search
    const [completedSubTab, setCompletedSubTab] = useState(0);
    // Sub-tab within In Progress: same indices
    const [inProgressSubTab, setInProgressSubTab] = useState(0);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [subscriptionTriggerReason, setSubscriptionTriggerReason] = useState('general');
    const [puzzles, setPuzzles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPuzzle, setSelectedPuzzle] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const pageSize = 10;

    const [playStats, setPlayStats] = useState({
        totalPuzzles: 0,
        completedPuzzles: 0,
        averageScore: 0,
    });

    useEffect(() => {
        trackEvent('entered', { name: 'LibraryMain' });
    }, []);

    useEffect(() => {
        const fetchPlayStats = async () => {
            try {
                const stats = await HomeService.getPlayStats();
                const rawAvg = stats?.averageScore;
                const averageScore =
                    typeof rawAvg === "number" && Number.isFinite(rawAvg) ? Math.round(rawAvg) : 0;
                setPlayStats({
                    totalPuzzles: stats.countPlay ?? 0,
                    completedPuzzles: stats.countFinished ?? 0,
                    averageScore,
                });
            } catch (e) {
                Logger.error("LibraryMain fetchPlayStats:", e);
            }
        };
        fetchPlayStats();
    }, []);

    useEffect(() => {
        const loadPuzzles = async () => {
            try {
                setPuzzles([]); // Start with empty puzzles, will be loaded per tab
            } catch (error) {
                Logger.error('Error loading puzzles:', error);
                setPuzzles([]);
            } finally {
                setLoading(false);
            }
        };

        loadPuzzles();
    }, []);

    // Load puzzles when active tab or page changes
    useEffect(() => {
        const loadPuzzlesForTab = async () => {
            if (activeTab === TAB_IN_PROGRESS) {
                // In Progress tab — handled by InProgressPanel
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                let viewerIsGuest = true;
                try {
                    const userData = await UserService.getUserInfo();
                    viewerIsGuest = !userData?.cloudUser;
                } catch {
                    viewerIsGuest = true;
                }
                const response = await getFilteredPuzzles();
                const raw = response.content || [];
                setPuzzles(viewerIsGuest ? sortPuzzlesFreeFirst(raw) : raw);
                setTotalPages(response.page?.totalPages ?? response.totalPages ?? 0);
                setTotalElements(response.page?.totalElements ?? response.totalElements ?? 0);
            } catch (error) {
                Logger.error('Error loading puzzles for tab:', error);
                setPuzzles([]);
                setTotalPages(0);
                setTotalElements(0);
            } finally {
                setLoading(false);
            }
        };

        loadPuzzlesForTab();
    }, [activeTab, completedSubTab, currentPage]);

    // Navigate to puzzle play page when a puzzle is selected
    useEffect(() => {
        if (selectedPuzzle) {
            const puzzleType = selectedPuzzle.type?.toUpperCase() || 'WORD_SEARCH';
            const puzzleId = selectedPuzzle.id;

            if (puzzleType === 'SLIDING') {
                navigate(`/puzzle/play-sliding/${encodeURIComponent(puzzleId)}`, {
                    state: {
                        fromLibrary: true,
                        activeTab: activeTab,
                        previousPath: '/account/library'
                    }
                });
                return;
            }

            if (puzzleType === 'JIGSAW') {
                navigate(getJigsawPlayBoardPath(puzzleId, selectedPuzzle.jigsawId), {
                    state: {
                        fromLibrary: true,
                        activeTab: activeTab,
                        previousPath: '/account/library'
                    }
                });
                return;
            }

            // Crossword puzzles use a different route
            if (puzzleType === 'CROSSWORD') {
                navigate(`/puzzle/play-cross/${puzzleId}`, {
                    state: {
                        fromLibrary: true,
                        activeTab: activeTab,
                        previousPath: '/account/library'
                    }
                });
            } else {
                const path =
                    puzzleType === "HIDDEN_MESSAGE"
                        ? `/puzzle/play-hidden-message/${puzzleId}`
                        : `/puzzle/play-word-search/${puzzleId}`;
                navigate(path, {
                    state: {
                        fromLibrary: true,
                        activeTab: activeTab,
                        previousPath: '/account/library'
                    }
                });
            }
        }
    }, [selectedPuzzle, navigate, activeTab]);

    // Restore active tab when returning from puzzle play
    useEffect(() => {
        if (location.state?.activeTab !== undefined) {
            setActiveTab(location.state.activeTab);
        }
    }, [location.state]);


    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setCurrentPage(0);
    };

    const handleSubTabChange = (event, newValue) => {
        setCompletedSubTab(newValue);
        setCurrentPage(0);
    };

    const handleInProgressSubTabChange = (event, newValue) => {
        setInProgressSubTab(newValue);
    };

    const handlePageChange = (event, page) => {
        setCurrentPage(page - 1); // MUI Pagination is 1-based, API is 0-based
    };

    const handlePuzzleSelect = (puzzle) => {
        // Show puzzle inline instead of navigating
        Logger.log("handlePuzzleSelect", puzzle);
        setSelectedPuzzle(puzzle);
    };

    const handleUpgradeRequired = (reason) => {
        setSubscriptionTriggerReason(reason);
        setSubscriptionModalOpen(true);
    };

    const handleSubscribe = () => {
        // Handle subscription logic
        setSubscriptionModalOpen(false);
    };

    const handleBackToLibrary = () => {
        setSelectedPuzzle(null);
    };

    const getFilteredPuzzles = async () => {
        switch (completedSubTab) {
            case 0: // Jigsaw — completed sessions
                try {
                    const jigsawResult = await JigsawPlayService.list({
                        page: currentPage,
                        pageSize,
                        sort: "updatedAt:desc",
                        isCompleted: true,
                    });
                    if (!jigsawResult.success) {
                        return { content: [], page: { totalElements: 0, totalPages: 0 } };
                    }
                    const jigsawData = jigsawResult.data;
                    const jigsawMapped = (jigsawData.content || []).map(jigsawPlayRoToCardPuzzle).filter(Boolean);
                    return {
                        content: jigsawMapped,
                        page: {
                            totalPages: jigsawData.totalPages ?? 0,
                            totalElements: jigsawData.totalElements ?? 0,
                        },
                    };
                } catch (error) {
                    Logger.error("Error fetching completed jigsaw puzzles:", error);
                    return { content: [], page: { totalElements: 0, totalPages: 0 } };
                }
            case 1: // Sliding — completed sessions
                try {
                    const data = await SlidingPlayService.listSlidingPlay({
                        page: currentPage,
                        pageSize,
                        sort: "updatedAt:desc",
                        isCompleted: true,
                    });
                    const mapped = (data.content || []).map(slidingPlayRoToCardPuzzle).filter(Boolean);
                    return {
                        content: mapped,
                        page: {
                            totalPages: data.totalPages ?? 0,
                            totalElements: data.totalElements ?? 0,
                        },
                    };
                } catch (error) {
                    Logger.error("Error fetching completed sliding puzzles:", error);
                    return { content: [], page: { totalElements: 0, totalPages: 0 } };
                }
            case 2: // Crossword
                try {
                    const response = await CrossService.requestPlayablePuzzles(
                        'CROSSWORD',
                        currentPage,
                        pageSize,
                        true,
                        true,
                        undefined,
                        'all',
                        false
                    );
                    return response;
                } catch (error) {
                    Logger.error('Error fetching CROSSWORD puzzles:', error);
                    return { content: [], totalElements: 0, totalPages: 0 };
                }
            case 3: // Hidden Message
                try {
                    const response = await GridService.requestPlayablePuzzles(
                        'HIDDEN_MESSAGE',
                        currentPage,
                        pageSize,
                        true,
                        true,
                        undefined,
                        'all',
                        false
                    );
                    return response;
                } catch (error) {
                    Logger.error('Error fetching HIDDEN_MESSAGE puzzles:', error);
                    return { content: [], totalElements: 0, totalPages: 0 };
                }
            case 4: // Word Search
                try {
                    const response = await GridService.requestPlayablePuzzles(
                        'WORD_SEARCH',
                        currentPage,
                        pageSize,
                        true,
                        true,
                        undefined,
                        'all',
                        false
                    );
                    return response;
                } catch (error) {
                    Logger.error('Error fetching WORD_SEARCH puzzles:', error);
                    return { content: [], totalElements: 0, totalPages: 0 };
                }
            case 5: // Word Wheel daily
                try {
                    const data = await WordWheelService.listDailyPlays({
                        page: currentPage,
                        pageSize,
                        sort: "updatedAt:desc",
                        isCompleted: true,
                    });
                    const mapped = (data.content || []).map(wordWheelPlayRoToCard).filter(Boolean);
                    return {
                        content: mapped,
                        page: {
                            totalPages: data.totalPages ?? 0,
                            totalElements: data.totalElements ?? 0,
                        },
                    };
                } catch (error) {
                    Logger.error("Error fetching Word Wheel daily puzzles:", error);
                    return { content: [], page: { totalElements: 0, totalPages: 0 } };
                }
            default:
                return { content: [], totalElements: 0, totalPages: 0 };
        }
    };

    const tabLabels = [
        t("library.tab-in-progress"),
        t("library.tab-completed"),
    ];

    const subTabLabels = [
        t("library.tab-jigsaw"),
        t("library.tab-sliding"),
        t("library.tab-crossword"),
        t("library.tab-hidden-message"),
        t("library.tab-word-search"),
        t("library.tab-word-wheel"),
    ];

    const contentLanguage = getContentLanguage();

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Show Puzzle or Library */}
            {selectedPuzzle ? (
                // Puzzle View
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Puzzle Header */}
                    <Box sx={{ p: 3, pb: 0, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={handleBackToLibrary}
                            sx={{ color: 'primary.main' }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                                {selectedPuzzle.title}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {selectedPuzzle.description}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Puzzle Content */}
                    <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                        {selectedPuzzle && (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                                flexDirection: 'column',
                                gap: 2
                            }}>
                                <Typography variant="h6" color="primary">
                                    {t("library.loading-puzzle")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("library.redirecting")}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            ) : (
                // Library View
                <>
                    {/* Header */}
                    <Box sx={{ p: 3, pb: 0, flexShrink: 0 }}>
                        <Box>
                            <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                                {t("library.title")}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {t("library.subtitle")}
                            </Typography>
                        </Box>
                        <Grid container spacing={3} sx={{ mt: 0.5, mb: 1 }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Paper sx={{ p: 2, textAlign: "center" }}>
                                    <Typography variant="h4" color="primary" gutterBottom>
                                        {playStats.totalPuzzles}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("profile.history.total-puzzles")}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Paper sx={{ p: 2, textAlign: "center" }}>
                                    <Typography variant="h4" color="success.main" gutterBottom>
                                        {playStats.completedPuzzles}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("profile.history.completed")}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Paper sx={{ p: 2, textAlign: "center" }}>
                                    <Typography variant="h4" color="warning.main" gutterBottom>
                                        {playStats.averageScore}%
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("profile.history.average-score")}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Tabs */}
                    <Box sx={{ px: 3, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                },
                                '& .MuiTab-root.Mui-selected': {
                                    color: NOTICE.info.text,
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: NOTICE.info.border,
                                }
                            }}
                        >
                            {tabLabels.map((label, index) => (
                                <Tab key={index} label={label} />
                            ))}
                        </Tabs>
                    </Box>

                    {/* Sub-tabs for In Progress */}
                    {activeTab === TAB_IN_PROGRESS && (
                        <Box sx={{ px: 3, py: 1.5, display: 'flex', gap: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                            {subTabLabels.map((label, index) => (
                                <Chip
                                    key={index}
                                    label={label}
                                    variant={inProgressSubTab === index ? "filled" : "outlined"}
                                    onClick={() => handleInProgressSubTabChange(null, index)}
                                    sx={{
                                        flex: 1,
                                        fontWeight: 500,
                                        ...(inProgressSubTab === index
                                            ? libraryFilterChipSelectedSx
                                            : { borderColor: 'divider', '&:hover': { backgroundColor: 'action.hover' } }),
                                    }}
                                />
                            ))}
                        </Box>
                    )}

                    {/* Sub-tabs for Completed */}
                    {activeTab === TAB_COMPLETED && (
                        <Box sx={{ px: 3, py: 1.5, display: 'flex', gap: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                            {subTabLabels.map((label, index) => (
                                <Chip
                                    key={index}
                                    label={label}
                                    variant={completedSubTab === index ? "filled" : "outlined"}
                                    onClick={() => handleSubTabChange(null, index)}
                                    sx={{
                                        flex: 1,
                                        fontWeight: 500,
                                        ...(completedSubTab === index
                                            ? libraryFilterChipSelectedSx
                                            : { borderColor: 'divider', '&:hover': { backgroundColor: 'action.hover' } }),
                                    }}
                                />
                            ))}
                        </Box>
                    )}

                    {/* Puzzle Grid */}
                    <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            {activeTab === TAB_IN_PROGRESS ? (
                                <InProgressPanel
                                    contentLanguage={contentLanguage}
                                    onPuzzleSelect={handlePuzzleSelect}
                                    onUpgradeRequired={handleUpgradeRequired}
                                    activeSubTab={inProgressSubTab}
                                />
                            ) : (
                                <CompletePanel
                                    puzzles={puzzles}
                                    contentLanguage={contentLanguage}
                                    onPuzzleSelect={handlePuzzleSelect}
                                    onUpgradeRequired={handleUpgradeRequired}
                                    isInProgress={false}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Pagination - Only show for Completed tab */}
                    {activeTab === TAB_COMPLETED && totalPages > 1 && (
                        <Box sx={{ p: 3, pt: 0, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                            <Stack spacing={2} alignItems="center">
                                <Pagination
                                    count={totalPages}
                                    page={currentPage + 1} // Convert 0-based to 1-based for MUI
                                    onChange={handlePageChange}
                                    size="large"
                                    sx={libraryPaginationSx}
                                    showFirstButton
                                    showLastButton
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {t("library.showing-puzzles", { count: puzzles.length, total: totalElements })}
                                </Typography>
                            </Stack>
                        </Box>
                    )}
                </>
            )}

            {/* Subscription Dialog */}
            <SubscriptionDialog
                open={subscriptionModalOpen}
                onClose={() => setSubscriptionModalOpen(false)}
                onSubscribe={handleSubscribe}
                triggerReason={subscriptionTriggerReason}
                userSubscription="free"
            />
        </Box>
    );
};

export default LibraryMain;

